import { BehaviorSubject, Observable, tap } from 'rxjs';

import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService, PushNotificationsService, StorageService, UserService } from '@core/services';
import { environment } from '@env/environment';
import {
  ApiBaseError,
  CodeApiResponse,
  LoginApiResponse,
  PhoneApiResponse,
  RegisterApiResponse,
  updateApiResponse,
  User,
  UserRegister,
  UserUpdate,
} from '@shared/models';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private pushNotificationsService: PushNotificationsService = inject(PushNotificationsService);

  #isAuthenticatedSubject = new BehaviorSubject<boolean | null>(null);
  isAuthenticated$ = this.#isAuthenticatedSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private storageService: StorageService,
    private userService: UserService,
    private router: Router,
  ) {
    this.#checkToken();
  }

  async isAuthenticated(): Promise<boolean> {
    const token = await this.storageService.get(environment.tokenName);
    return token ? true : false;
  }

  login(email: string, password: string, device_session_id: string): Observable<LoginApiResponse> {
    return this.apiService.post<LoginApiResponse>('auth/login', { email, password, device_session_id }).pipe(
      tap((response) => {
        if (response.access_token) {
          this.#isAuthenticatedSubject.next(true);
          this.storageService.set(environment.tokenName, response.access_token);
          if (response.user) {
            this.userService.setUser(response.user);
            this.pushNotificationsService.register(response.user.id.toString());
          }
        }
      }),
    );
  }

  register(userData: UserRegister): Observable<RegisterApiResponse> {
    return this.apiService.post<RegisterApiResponse>('auth/register', userData).pipe(
      tap((response) => {
        if (response.access_token) {
          this.#isAuthenticatedSubject.next(true);
          this.storageService.set(environment.tokenName, response.access_token);
          if (response.user) {
            this.userService.setUser(response.user);
            this.pushNotificationsService.register(response.user.id.toString());
          }
        }
      }),
    );
  }

  loginSocial(provider: string, payload: UserRegister): Observable<CodeApiResponse> {
    return this.apiService.post<CodeApiResponse>('auth/' + provider, payload).pipe(
      tap(async (response) => {
        if (response.access_token) {
          this.#isAuthenticatedSubject.next(true);
          this.storageService.set(environment.tokenName, response.access_token);
          if (response.user) {
            this.userService.setUser(response.user);
            this.pushNotificationsService.register(response.user.id.toString());
          }
        }
        if (response.action) {
          if (response.action === 'register') {
            await this.storageService.set('register', provider);
            await this.storageService.set('registerData', payload);
            this.router.navigateByUrl('/auth/register');
          }
        }
      }),
    );
  }

  registerSocial(provider: string, payload: UserRegister): Observable<CodeApiResponse> {
    return this.apiService.post<CodeApiResponse>('auth/' + provider + '/callback', payload).pipe(
      tap((response) => {
        if (response.access_token) {
          this.#isAuthenticatedSubject.next(true);
          this.storageService.set(environment.tokenName, response.access_token);
          if (response.user) {
            this.userService.setUser(response.user);
            this.pushNotificationsService.register(response.user.id.toString());
          }
        }
      }),
    );
  }

  phoneResend(phone: string, voice: string): Observable<PhoneApiResponse> {
    return this.apiService.post<PhoneApiResponse>('auth/phone/resend', { phone, voice }).pipe(
      tap((response) => {
        if (response.data) {
          //
        }
      }),
    );
  }

  phone(phone: string, voice: string): Observable<PhoneApiResponse> {
    return this.apiService.post<PhoneApiResponse>('auth/phone/send', { phone, voice }).pipe(
      tap((response) => {
        if (response.data) {
          //
        }
      }),
    );
  }

  code(phone: string, code: string, device_session_id: string): Observable<CodeApiResponse> {
    return this.apiService.post<CodeApiResponse>('auth/phone/verify', { phone, code, device_session_id }).pipe(
      tap((response) => {
        if (response.access_token) {
          this.#isAuthenticatedSubject.next(true);
          this.storageService.set(environment.tokenName, response.access_token);
          if (response.user) {
            this.userService.setUser(response.user);
            this.pushNotificationsService.register(response.user.id.toString());
          }
        }
      }),
    );
  }

  update(userData: UserUpdate): Observable<updateApiResponse> {
    return this.apiService.put<updateApiResponse>('user/profile', userData).pipe();
  }

  profile(): Observable<User> {
    return this.apiService.get<User>('user/profile').pipe(
      tap((response) => {
        this.userService.setUser(response);
      }),
    );
  }

  logout(): Observable<ApiBaseError> {
    return this.apiService.post<ApiBaseError>('auth/logout', {}).pipe(
      tap(() => {
        this.removeSession();
      }),
    );
  }

  delete(): Observable<ApiBaseError> {
    return this.apiService.delete<ApiBaseError>('user/profile/delete').pipe(
      tap((response) => {
        if (response.message) {
          this.removeSession();
        }
      }),
    );
  }

  removeSession(): void {
    this.#isAuthenticatedSubject.next(false);
    this.storageService.remove('skipReview');
    this.storageService.remove(environment.tokenName);
    this.userService.setUser(null);
    this.pushNotificationsService.unregister();
  }

  async #checkToken(): Promise<void> {
    const token = await this.storageService.get(environment.tokenName);
    if (token) {
      this.#isAuthenticatedSubject.next(true);
    } else {
      this.#isAuthenticatedSubject.next(false);
    }
  }
}
